import { useEffect } from 'react'
import { ChatInputElementId } from '../chat-input-element-id'

/**
 * Type anywhere and the composer takes focus. `disabled` is for the window
 * where an open find bar's input has been blurred to nowhere by Chromium's
 * find internals — without it the next keystroke lands here and wins the focus
 * fight before the find bar's resync can reclaim it.
 */
export const useTypeToFocusChatInput = (disabled?: boolean) => {
  useEffect(() => {
    if (disabled) {
      return
    }

    const onDocumentKeyDown = (e: KeyboardEvent) => {
      const el = document.getElementById(ChatInputElementId)
      if (!el) {
        return
      }

      // Don't hijack focus from the composer itself, or from any other input,
      // textarea, or contenteditable the user is typing in.
      const active = document.activeElement
      if (
        active?.id === ChatInputElementId ||
        active?.tagName === 'INPUT' ||
        active?.tagName === 'TEXTAREA' ||
        active?.getAttribute('contenteditable') === 'true'
      ) {
        return
      }

      // Paste (Cmd+V / Ctrl+V) focuses too, so clipboard content lands in the input.
      const isPaste = (e.metaKey || e.ctrlKey) && e.key === 'v'
      // Printable characters only — not arrows, Tab, Shift, Enter, Escape, or F-keys.
      const isFunctionKey = e.key.startsWith('F') && e.key.length >= 2 && e.key.length <= 3
      const isPrintable = e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey && !isFunctionKey

      if (isPaste || isPrintable) {
        el.focus()
      }
    }
    document.addEventListener('keydown', onDocumentKeyDown)
    return () => {
      document.removeEventListener('keydown', onDocumentKeyDown)
    }
  }, [disabled])
}
